import { useCallback, useEffect, useState, type ReactNode } from "react";
import type { AdminApi } from "@/api";
import type { SamsAccessState } from "@/components/layout/AdminShellContext";
import { Banner, Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import type {
  CandidateSignal,
  CandidateSourceEnvironment,
  RereviewResult,
  RescanResult,
  ReviewStageState,
  SignalStages,
  TcStageItem,
  TcStageState,
} from "@/types/issuePipeline";
import { AnalysisConversation, ManualPromote, ReviewJudgment, promotable } from "./BatchDetections";
import { type DatePreset, dateRange, signalKey } from "./candidateFeed";
import { DetectionControls } from "./DetectionControls";
import { FeedbackOnlyPanel, feedbackOnlyCount, useFeedbackOnly } from "./FeedbackOnlyPanel";
import { CandidateDetail, detectionBlocked } from "./IssueCandidatesPage";
import { pipelineError } from "./pipelineShared";
import { RetryButton, RetryNote, retryFailure, retryLine, type RetryLine } from "./RetryButton";

type Tab = "signals" | "review" | "tc" | "feedback";
type Sub = "" | ReviewStageState | TcStageState;
type Env = "all" | CandidateSourceEnvironment;
type Period = Extract<DatePreset, "all" | "week" | "today">;

const PAGE = 10;
const TABS: Array<[Tab, string]> = [["signals", "신호 탐지"], ["review", "정밀 탐지"], ["tc", "TC"], ["feedback", "피드백"]];
const ENVS: Array<[Env, string]> = [["all", "전체"], ["prod", "운영"], ["dev", "개발"]];
const PERIODS: Array<[Period, string]> = [["all", "전체"], ["week", "7일"], ["today", "오늘"]];

type Tone = "ok" | "bad" | "warn" | "grey";
const TONES: Record<Tone, string> = {
  ok: "border-success-line bg-success-surface text-success",
  bad: "border-destructive-line bg-destructive-surface text-destructive",
  warn: "border-accent-line bg-accent text-accent-foreground",
  grey: "border-border bg-surface-input text-muted-foreground",
};
const REVIEW_STATES: Record<ReviewStageState, [Tone, string]> = {
  pass: ["ok", "통과"], fail: ["grey", "불통과"], error: ["bad", "오류"], pending: ["warn", "대기"],
};
const TC_STATES: Record<TcStageState, [Tone, string]> = { done: ["ok", "등록됨"], error: ["bad", "오류"], wait: ["warn", "대기"] };
const SUBS: Partial<Record<Tab, Sub[]>> = { review: ["pass", "fail", "error", "pending"], tc: ["done", "error", "wait"] };

/** 정밀 탐지 verdicts, in the reviewer's words. */
const VERDICTS: Record<string, string> = {
  discomfort: "진짜 문제",
  no_issue: "문제 없음",
  request_change: "고쳐 달라는 새 요청",
  other_target: "다른 사람에게 한 말",
  ai_not_invoked: "AI가 답한 대화가 아님",
  insufficient_information: "판단할 정보 부족",
};
/** Why an error or waiting row of 정밀 탐지 is one (`stage.review.code`). */
const REVIEW_ERRORS: Record<string, string> = {
  NO_REVIEW: "아직 정밀 탐지 전",
  REVIEW_NOT_FINISHED: "정밀 탐지가 아직 끝나지 않음",
  REVIEW_FAILED: "판정 실패",
  PASS_NOW_FAILED: "통과했다가 다시 판정이 실패함",
  PASS_NOW_REJECTED: "통과했다가 다시 판정에서 불통과",
};

const KST_DAY = new Intl.DateTimeFormat("ko-KR", { timeZone: "Asia/Seoul", month: "numeric", day: "numeric" });
const shortDay = (value: string) => KST_DAY.format(new Date(value)).replace(/\.\s?/g, "/").replace(/\/$/, "");

const STRENGTH: Record<string, string> = { low: "약함", medium: "중간", high: "강함" };
/** The 1차 type codes in words (SAMS `eval_signal_item.TYPES`). */
const SIGNAL_TYPES: Record<string, string> = {
  complaint: "불만", correction: "정정", rebuttal: "반박", premise: "전제/역할 지적", repeat: "같은 요청 반복",
  recheck: "재확인 요구", no_response: "무응답·멈춤 지적", frustration: "짜증·실망", other: "그 밖",
};

/**
 * 신호 탐지's reason. A one-message scan stores its type and strength
 * (`정정(correction), confidence=low`), a routed bundle `correction (low)`; with a prompt that asks
 * why, each ends in `: <one line>`. A bundled Gemini scan or a user action stores a sentence.
 */
export function scanReason(reason: string | null): { summary: string; detail: string } {
  if (!reason) return { summary: "이유 기록 없음", detail: "이유를 남기지 않음" };
  const text = reason.trim();
  const item = /^(.*?\S)\(([a-z_]+)\)(?:, confidence=(\w+))?(?::\s*(.+))?$/s.exec(text);
  const routed = item ? null : /^([a-z_]+) \((\w+)\)(?::\s*(.+))?$/s.exec(text);
  if (!item && !routed) return { summary: text, detail: text };
  const label = item ? item[1] : SIGNAL_TYPES[routed![1]] ?? routed![1];
  const level = item ? item[3] : routed![2] === "unknown" ? undefined : routed![2];
  const line = item ? item[4] : routed![3];
  const kind = `${label}${level ? ` · 세기 ${STRENGTH[level] ?? level}` : ""}`;
  return line ? { summary: line, detail: `${line} (유형 ${kind})` }
    : { summary: label, detail: `유형만 남김 (${kind}) · 설명 문장 없음` };
}

function reviewSummary(signal: CandidateSignal) {
  const stage = signal.stage?.review;
  if (!stage) return "정밀 탐지 결과 정보 없음";
  if (stage.state === "pass" || stage.state === "fail") return stage.reason || VERDICTS[stage.verdict ?? ""] || "이유 기록 없음";
  const label = REVIEW_ERRORS[stage.code ?? ""] ?? stage.code ?? "오류";
  return stage.reason ? `${label} · ${stage.reason}` : label;
}

function rereviewLine(result: RereviewResult): RetryLine {
  const line = retryLine(result);
  if (!result.changed) return line;
  const before = VERDICTS[result.previous_verdict ?? ""] ?? result.previous_verdict ?? "기록 없음";
  return { ...line, text: `${line.text} · 다시 판정 결과 다름 (이전: ${before})` };
}

const RESCAN_RESULTS: Record<RescanResult["result"], string> = { signal: "여전히 신호", not_signal: "신호 아님", error: "오류" };

function rescanLine(result: RescanResult): RetryLine {
  return { text: `${RESCAN_RESULTS[result.result]} · ${result.message}`, error: result.result === "error" };
}

/** The latest rescan, whatever its state: a later failure or one still running is never an earlier result. */
export function rescanNote(rescan: CandidateSignal["rescan"]) {
  if (!rescan) return null;
  const said = rescan.status === "pending" || rescan.status === "running" ? "진행 중"
    : rescan.still_signal === null ? "오류" : rescan.still_signal ? "여전히 신호" : "신호 아님";
  return `다시 훑음 ${shortDay(rescan.at)}: ${said}`;
}

function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={cn("rounded-full border px-2 py-0.5 text-xs font-semibold whitespace-nowrap", TONES[tone])}>{children}</span>;
}

/** A button that runs one action now and hands back its result line; disabled while it runs. */
function RunButton<T>({ label, busyLabel, run, toLine, onSettled }: {
  label: string; busyLabel: string; run: () => Promise<T>; toLine: (result: T) => RetryLine;
  onSettled: (line: RetryLine) => void;
}) {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (busy) return;
    setBusy(true);
    let line: RetryLine;
    try {
      line = toLine(await run());
    } catch (err) {
      line = retryFailure(err);
    }
    setBusy(false);
    onSettled(line);
  };
  return <Button variant="outline" size="sm" disabled={busy} onClick={() => void click()}>{busy ? busyLabel : label}</Button>;
}

function Facts({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg bg-surface-subtle p-3 text-[13px]">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0 break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const ids = (...values: Array<string | null | undefined>) => (
  <span className="font-mono text-xs">{values.filter(Boolean).join(" · ")}</span>
);

/** One list row: date · why the stage judged so · its state in one word; the detail opens below. */
function Row({ open, onToggle, at, summary, sub, tone, state, children }: {
  open: boolean; onToggle: () => void; at: string; summary: ReactNode; sub?: ReactNode; tone: Tone; state: string; children: ReactNode;
}) {
  return (
    <div className="border-b border-border last:border-b-0">
      <button type="button" aria-expanded={open} onClick={onToggle}
        className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-2.5 px-3.5 py-2.5 text-left focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring">
        <span className="text-xs text-muted-foreground tabular-nums">{shortDay(at)}</span>
        <span className="min-w-0">
          <span className="block truncate text-sm">{summary}</span>
          {sub && <span className="block truncate text-xs text-muted-foreground">{sub}</span>}
        </span>
        <Pill tone={tone}>{state}</Pill>
      </button>
      {open && <div className="grid gap-2.5 px-3.5 pb-3">{children}</div>}
    </div>
  );
}

function Tile({ label, value, active, onOpen, children }: {
  label: string; value: number | null; active: boolean; onOpen: () => void; children?: ReactNode;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col items-start rounded-xl border bg-card p-3", active ? "border-primary ring-1 ring-primary" : "border-border")}>
      <button type="button" className="flex w-full flex-col items-start text-left" onClick={onOpen}>
        <span className="text-[13px] text-muted-foreground">{label}</span>
        <span className="text-[28px] leading-tight font-bold tabular-nums sm:text-[34px]">{value ?? "—"}</span>
      </button>
      <span className="flex min-h-4.5 flex-wrap gap-x-1.5 text-xs text-muted-foreground">{children}</span>
    </div>
  );
}

function TileLink({ label, value, bad, onOpen }: { label: string; value: number | null; bad?: boolean; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen}
      className={cn("underline underline-offset-2", bad && value ? "font-semibold text-destructive" : "")}>
      {label} <b className="tabular-nums">{value ?? "—"}</b>
    </button>
  );
}

const Arrow = () => <span aria-hidden className="size-2 rotate-45 self-center justify-self-center border-t-2 border-r-2 border-muted-foreground" />;

/**
 * 문제 식별 (user decision 2026-10-21): 신호 탐지 › 정밀 탐지 › TC, and the 👎 kept only as
 * feedback. Every number is the server's (`/signals/stages`, `/signals`, feedback-only), on one
 * period basis, the day a signal was first found. Each row says why its stage judged so and
 * offers that stage's run again: 신호 탐지 다시, 정밀 탐지 다시, 다시 시도.
 */
export function ProblemIdentificationPage({ api, access }: { api: AdminApi; access: SamsAccessState }) {
  const [env, setEnv] = useState<Env>("all");
  const [period, setPeriod] = useState<Period>("all");
  const [tab, setTab] = useState<Tab>("tc");
  const [sub, setSub] = useState<Sub>("");
  const [open, setOpen] = useState<string | null>(null);
  const [stages, setStages] = useState<SignalStages | null>(null);
  const [stagesError, setStagesError] = useState("");
  const [signals, setSignals] = useState<CandidateSignal[]>([]);
  const [signalTotal, setSignalTotal] = useState<number | null>(null);
  // The cursor of each page shown so far: [이전] goes back without asking for counts again.
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");
  // Run-again results by row, kept across reloads: the row itself may leave the list (VERIFY-55, VERIFY-57).
  const [notes, setNotes] = useState<Record<string, { line: RetryLine; label: string }>>({});
  const [showDetection, setShowDetection] = useState(false);
  // [신호 탐지 실행] follows SAMS access as on the earlier screen (UIDETECTBTN); SAMS checks each call again.
  const detectionOff = detectionBlocked(access);
  const [selected, setSelected] = useState<string | null>(() =>
    typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("candidate"));

  const sourceEnv = env === "all" ? undefined : env;
  const { date_from, date_to } = dateRange({ preset: period, from: "", to: "" });
  const feedback = useFeedbackOnly(api, sourceEnv, tab === "feedback");
  const signalTab = tab === "signals" || tab === "review";

  const loadStages = useCallback(async () => {
    setStagesError("");
    try {
      setStages(await api.getSignalStages({ source_environment: sourceEnv, date_from, date_to }));
    } catch (err) {
      setStages(null);
      setStagesError(pipelineError(err));
    }
  }, [api, sourceEnv, date_from, date_to]);

  const loadSignals = useCallback(async (at: number, cursor: string | null) => {
    setLoading(true);
    setListError("");
    try {
      const result = await api.listCandidateSignals({
        source_environment: sourceEnv, date_from, date_to, outcome: "all",
        review_state: tab === "review" && sub ? (sub as ReviewStageState) : undefined, cursor, limit: PAGE,
      });
      setSignals(result.items);
      setSignalTotal(result.total);
      setNextCursor(result.next_cursor);
      setPage(at);
    } catch (err) {
      setSignals([]);
      setSignalTotal(null);
      setNextCursor(null);
      setListError(pipelineError(err));
    } finally {
      setLoading(false);
    }
  }, [api, sourceEnv, date_from, date_to, tab, sub]);

  const { load: loadFeedback } = feedback;
  useEffect(() => {
    void loadStages();
    void loadFeedback();
  }, [loadStages, loadFeedback]);
  useEffect(() => {
    setPage(0);
    setCursors([null]);
    if (signalTab) void loadSignals(0, null);
  }, [signalTab, loadSignals]);

  const choose = (nextTab: Tab, nextSub: Sub = "") => {
    setTab(nextTab);
    setSub(nextSub);
    setOpen(null);
  };
  const reload = () => {
    void loadStages();
    if (signalTab) void loadSignals(page, cursors[page] ?? null);
  };
  const keep = (key: string, label: string) => (line: RetryLine) => {
    setNotes((current) => ({ ...current, [key]: { line, label } }));
    reload();
  };

  const review = stages?.signals.review;
  const tc = stages?.tc;
  const feedbackCount = feedbackOnlyCount(feedback.state);
  const feedbackNumber = typeof feedbackCount === "number" ? feedbackCount : null;
  const tabCount: Record<Tab, number | null> = {
    signals: stages?.signals.total ?? null, review: stages?.signals.total ?? null,
    tc: tc?.total ?? null, feedback: feedbackNumber,
  };
  const subCount = (s: Sub): number | null => {
    if (!s) return tabCount[tab];
    if (tab === "review") return review?.[s as ReviewStageState] ?? null;
    return tc?.[s as TcStageState] ?? null;
  };

  const tcRows = (stages?.tc_items ?? []).filter((item) => !sub || item.stage.tc.state === sub);
  const tcPages = Math.max(1, Math.ceil(tcRows.length / PAGE));
  const tcPage = Math.min(page, tcPages - 1);
  const shownTotal = signalTab ? signalTotal : tab === "tc" ? tcRows.length : null;
  const first = (signalTab ? page : tcPage) * PAGE;
  const shown = signalTab ? signals.length : Math.min(PAGE, tcRows.length - first);
  const hasNext = signalTab ? Boolean(nextCursor) : tcPage < tcPages - 1;
  const toggle = (key: string) => setOpen((current) => (current === key ? null : key));

  const signalRow = (signal: CandidateSignal) => {
    const environment = signal.source_environment as CandidateSourceEnvironment;
    const stage = signal.stage?.review;
    const contextId = signal.review?.analysis_id ?? signal.scan_analysis_id;
    if (tab === "signals") {
      const key = `s:${signalKey(signal)}`;
      return (
        <Row key={key} open={open === key} onToggle={() => toggle(key)} at={signal.detected_at}
          summary={scanReason(signal.reason).summary} sub={rescanNote(signal.rescan)} tone="grey" state="넘어감">
          <Facts rows={[
            ["신호 탐지가 남긴 것", scanReason(signal.reason).detail],
            ...(signal.rescan ? [["다시 훑은 결과", `${rescanNote(signal.rescan)}${signal.rescan.reason
              ? ` · ${scanReason(signal.rescan.reason).summary}` : ""} (목록과 TC는 그대로)`] as [string, ReactNode]] : []),
            ["정밀 탐지 결과", stage ? REVIEW_STATES[stage.state][1] : "정보 없음"],
            ["환경", signal.source_environment],
            ["기록", ids(signal.signal_message_id, signal.scan_analysis_id)],
          ]} />
          <div className="flex flex-wrap items-center gap-2">
            <RunButton label="신호 탐지 다시" busyLabel="신호 탐지 다시 하는 중…"
              run={() => api.rescanSignal(signal.signal_message_id, environment)} toLine={rescanLine}
              onSettled={keep(key, `신호 탐지 다시 (${signal.source_environment} · ${scanReason(signal.reason).summary})`)} />
          </div>
          {notes[key] && <RetryNote line={notes[key].line} />}
          {signal.scan_analysis_id && <Conversation api={api} analysisId={signal.scan_analysis_id} />}
        </Row>
      );
    }
    const key = `r:${signalKey(signal)}`;
    const state = stage?.state ?? "error";
    return (
      <Row key={key} open={open === key} onToggle={() => toggle(key)} at={signal.detected_at}
        summary={reviewSummary(signal)} tone={REVIEW_STATES[state][0]} state={REVIEW_STATES[state][1]}>
        <Facts rows={[
          [state === "error" ? "오류" : state === "pending" ? "대기" : "판정 이유", reviewSummary(signal)],
          ...(stage?.verdict ? [["판정", VERDICTS[stage.verdict] ?? stage.verdict] as [string, ReactNode]] : []),
          ["환경", signal.source_environment],
          ["기록", <>{ids(signal.signal_message_id, signal.candidate_id, signal.review?.analysis_id)}
            {signal.review && signal.review.attempts > 1 ? ` · 시도 ${signal.review.attempts}번` : ""}</>],
        ]} />
        <div className="flex flex-wrap items-start gap-2">
          <RunButton label="정밀 탐지 다시" busyLabel="정밀 탐지 다시 하는 중…"
            run={() => api.rereviewSignal(signal.signal_message_id, environment)} toLine={rereviewLine}
            onSettled={keep(key, `정밀 탐지 다시 (${signal.source_environment} · ${reviewSummary(signal)})`)} />
          {state !== "pass" && promotable(signal) && (
            <ManualPromote api={api} signal={signal} onPromoted={(candidateId) => { setSelected(candidateId); reload(); }} />
          )}
        </div>
        {notes[key] && <RetryNote line={notes[key].line} />}
        {signal.review && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">판정 원문</summary>
            <div className="mt-2"><ReviewJudgment review={signal.review} /></div>
          </details>
        )}
        {contextId && <Conversation api={api} analysisId={contextId} />}
      </Row>
    );
  };

  const tcRow = (item: TcStageItem) => {
    const key = `c:${item.candidate_id}`;
    const { state, code, reason } = item.stage.tc;
    const feedbackOrigin = item.origin === "feedback";
    return (
      <Row key={key} open={open === key} onToggle={() => toggle(key)} at={item.found_at}
        summary={<><span className="mr-1.5 rounded border border-border px-1 text-[11px] text-muted-foreground">{feedbackOrigin ? "👎" : "정밀 탐지"}</span>{item.description}</>}
        sub={reason ?? (state === "wait" ? "TC 만들기 시도 기록 없음" : code)}
        tone={TC_STATES[state][0]} state={TC_STATES[state][1]}>
        <Facts rows={[
          ["출처", feedbackOrigin ? "👎" : "정밀 탐지 통과"],
          [state === "error" ? "오류" : "이유", reason ?? (state === "done" ? "TC 등록됨" : "TC 만들기 시도 기록 없음")],
          ["환경", item.source_environment],
          ["기록", ids(item.candidate_id, item.signal_message_id, code)],
        ]} />
        <div className="flex flex-wrap items-start gap-2">
          {state !== "done" && (
            <RetryButton run={() => api.retryCandidate(item.candidate_id)} onSettled={(line) => keep(key, `다시 시도 (${item.description})`)(line)} />
          )}
          {item.test_case_id && (
            <a className="self-center text-sm text-link hover:underline" href={`/test-cases/all?tc=${encodeURIComponent(item.test_case_id)}`}>TC 열기 →</a>
          )}
          <Button variant="outline" size="sm" onClick={() => setSelected(item.candidate_id)}>대화와 기록 보기</Button>
        </div>
        {notes[key] && <RetryNote line={notes[key].line} />}
      </Row>
    );
  };

  const list = signalTab ? signals.map(signalRow)
    : tab === "tc" ? tcRows.slice(first, first + PAGE).map(tcRow) : [];
  const prefix = { signals: "s:", review: "r:", tc: "c:", feedback: "-" }[tab];
  const listed = new Set(list.map((row) => row.key));
  const orphans = Object.entries(notes).filter(([key]) => key.startsWith(prefix) && !listed.has(key));

  return (
    <div className={cn("grid items-start gap-4", selected && "xl:grid-cols-[minmax(320px,1fr)_minmax(440px,1.2fr)]")}>
      <div className="grid gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Segment label="환경" options={ENVS} value={env} onChange={(value) => { setEnv(value); setOpen(null); }} />
          <Segment label="기간" options={PERIODS} value={period} onChange={(value) => { setPeriod(value); setOpen(null); }} />
          <span className="ml-auto text-xs text-muted-foreground">처음 찾은 날 기준</span>
        </div>
        {stagesError && <Banner tone="error">단계별 수를 불러오지 못했습니다 · {stagesError}</Banner>}
        <div className="grid grid-cols-[1fr_14px_1fr_14px_1fr] items-stretch gap-1">
          <Tile label="신호 탐지" value={stages?.signals.total ?? null} active={tab === "signals"} onOpen={() => choose("signals")} />
          <Arrow />
          <Tile label="정밀 탐지 통과" value={review?.pass ?? null} active={tab === "review" && sub === "pass"} onOpen={() => choose("review", "pass")}>
            <TileLink label="불통과" value={review?.fail ?? null} onOpen={() => choose("review", "fail")} />
            <TileLink label="오류" value={review?.error ?? null} bad onOpen={() => choose("review", "error")} />
            {Boolean(review?.pending) && <TileLink label="대기" value={review!.pending} onOpen={() => choose("review", "pending")} />}
          </Tile>
          <Arrow />
          <Tile label="TC" value={tc?.done ?? null} active={tab === "tc" && sub === "done"} onOpen={() => choose("tc", "done")}>
            <TileLink label="대기" value={tc?.wait ?? null} onOpen={() => choose("tc", "wait")} />
            <TileLink label="오류" value={tc?.error ?? null} bad onOpen={() => choose("tc", "error")} />
          </Tile>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" className="rounded-full" disabled={detectionOff !== null}
            aria-expanded={showDetection && detectionOff === null} onClick={() => setShowDetection((value) => !value)}>
            신호 탐지 실행
          </Button>
          <Button variant="outline" size="sm" className={cn("rounded-full", tab === "feedback" && "border-primary ring-1 ring-primary")}
            aria-pressed={tab === "feedback"} onClick={() => choose("feedback")}>
            피드백 <b className="tabular-nums">{feedbackNumber ?? "—"}</b>
          </Button>
        </div>
        {detectionOff && <p className="text-right text-xs text-muted-foreground">{detectionOff}</p>}
        {showDetection && access.state === "admin" && <DetectionControls api={api} prodAccess={access.me.prod_access === true} />}
        <section aria-label="단계별 목록" aria-live="polite" className="overflow-hidden rounded-xl border border-border bg-card">
          <div role="tablist" className="flex gap-1 overflow-x-auto border-b border-border px-2.5 py-2">
            {TABS.map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => choose(id)}
                className={cn("rounded-md px-2 py-1 text-[13px] whitespace-nowrap", tab === id ? "bg-muted font-semibold text-foreground" : "text-muted-foreground")}>
                {label}<b className="ml-1 tabular-nums">{tabCount[id] ?? "—"}</b>
              </button>
            ))}
          </div>
          {SUBS[tab] && (
            <div className="flex gap-1 overflow-x-auto border-b border-border px-2.5 py-1.5">
              {([""] as Sub[]).concat(SUBS[tab]!).map((s) => (
                <button key={s || "all"} type="button" aria-pressed={sub === s} onClick={() => choose(tab, s)}
                  className={cn("rounded-full border px-2 py-0.5 text-xs whitespace-nowrap",
                    sub === s ? "border-foreground font-semibold text-foreground" : "border-border text-muted-foreground")}>
                  {s ? (tab === "review" ? REVIEW_STATES[s as ReviewStageState] : TC_STATES[s as TcStageState])[1] : "전체"}{" "}
                  <b className="tabular-nums">{subCount(s) ?? "—"}</b>
                </button>
              ))}
            </div>
          )}
          {tab === "feedback" ? (
            <div className="grid gap-2 p-3">
              <p className="text-xs text-muted-foreground">TC가 되지 않는 👎(AI가 먼저 보낸 메시지에 누른 👎)만 보입니다. 기간과 무관한 현재 기록입니다.</p>
              <FeedbackOnlyPanel state={feedback.state} omitted={feedback.omitted} onMore={(environment) => void feedback.more(environment)} />
            </div>
          ) : (
            <>
              {listError && <div className="p-3"><Banner tone="error">{listError}</Banner></div>}
              {orphans.length > 0 && (
                <div className="grid gap-1 border-b border-border px-3.5 py-2">
                  {orphans.map(([key, note]) => <RetryNote key={key} line={note.line} label={note.label} />)}
                </div>
              )}
              {list}
              {loading && <p role="status" className="px-3.5 py-3 text-sm">불러오는 중…</p>}
              {!loading && !listError && list.length === 0 && <p className="px-3.5 py-6 text-center text-sm text-muted-foreground">없음</p>}
              {shownTotal !== null && shownTotal > PAGE && (
                <div className="flex items-center justify-between gap-2 border-t border-border px-3.5 py-2.5 text-[13px] text-muted-foreground tabular-nums">
                  <span>{first + 1}–{first + shown} / {shownTotal}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={loading || (signalTab ? page : tcPage) === 0} onClick={() => {
                      setOpen(null);
                      if (signalTab) void loadSignals(page - 1, cursors[page - 1] ?? null);
                      else setPage(tcPage - 1);
                    }}>이전</Button>
                    <Button variant="outline" size="sm" disabled={loading || !hasNext} onClick={() => {
                      setOpen(null);
                      if (signalTab) {
                        setCursors((current) => [...current.slice(0, page + 1), nextCursor]);
                        void loadSignals(page + 1, nextCursor);
                      } else setPage(tcPage + 1);
                    }}>다음</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
      {selected && (
        <CandidateDetail key={selected} api={api} id={selected} onClose={() => setSelected(null)}
          onReviewed={() => void loadStages()} onSplit={(candidate) => { setSelected(candidate.candidate_id); void loadStages(); }} />
      )}
    </div>
  );
}

function Segment<T extends string>({ label, options, value, onChange }: {
  label: string; options: Array<[T, string]>; value: T; onChange: (value: T) => void;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex rounded-lg bg-muted p-0.5">
      {options.map(([id, text]) => (
        <button key={id} type="button" aria-pressed={value === id} onClick={() => onChange(id)}
          className={cn("rounded-md px-2.5 py-1 text-[13px]", value === id ? "bg-card font-semibold text-foreground shadow-sm" : "text-muted-foreground")}>
          {text}
        </button>
      ))}
    </div>
  );
}

/** The analysed messages, read from the source only when asked (a prod row needs prod access). */
function Conversation({ api, analysisId }: { api: AdminApi; analysisId: string }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="grid gap-2">
      <Button className="justify-self-start" variant="ghost" size="sm" aria-expanded={shown} onClick={() => setShown((value) => !value)}>
        {shown ? "대화 닫기" : "대화 보기"}
      </Button>
      {shown && <AnalysisConversation api={api} analysisId={analysisId} />}
    </div>
  );
}
